import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCategoryNav = (url: string) => {
    setMenuOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-16">
        <Link to="/" className="flex items-center gap-2">
          <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
            Itaobuy
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm font-label text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-home-4-line mr-1.5"></i>
            Home
          </Link>
          <Link to="/articoli/guida-itaobuy-spreadsheet" className="text-sm font-label text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-book-open-line mr-1.5"></i>
            Guida
          </Link>
          <Link to="/articoli/guida-acquisti-itaobuy" className="text-sm font-label text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-shopping-bag-3-line mr-1.5"></i>
            Guida Acquisti
          </Link>
          <a href="#categorie" className="text-sm font-label text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-grid-line mr-1.5"></i>
            Categorie
          </a>
          <a href="#articoli" className="text-sm font-label text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-article-line mr-1.5"></i>
            Articoli
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="text-sm font-label bg-primary-500 text-background-50 px-4 py-2 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            buy it now
          </a>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-foreground-950`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 pb-6 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-0.5 pt-3">
            <Link
              to="/"
              className="flex items-center gap-3 text-sm font-label text-foreground-800 py-3 px-3 rounded-lg hover:bg-accent-100/40 transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              <i className="ri-home-4-line text-lg text-foreground-500"></i>
              Home
            </Link>
            <Link
              to="/articoli/guida-itaobuy-spreadsheet"
              className="flex items-center gap-3 text-sm font-label text-foreground-800 py-3 px-3 rounded-lg hover:bg-accent-100/40 transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              <i className="ri-book-open-line text-lg text-foreground-500"></i>
              Guida Completa
            </Link>
            <Link
              to="/articoli/guida-acquisti-itaobuy"
              className="flex items-center gap-3 text-sm font-label text-foreground-800 py-3 px-3 rounded-lg hover:bg-accent-100/40 transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              <i className="ri-shopping-bag-3-line text-lg text-foreground-500"></i>
              Guida Acquisti
            </Link>
            <Link
              to="/articoli/sicurezza-itaobuy-spreadsheet"
              className="flex items-center gap-3 text-sm font-label text-foreground-800 py-3 px-3 rounded-lg hover:bg-accent-100/40 transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              <i className="ri-shield-check-line text-lg text-foreground-500"></i>
              Sicurezza
            </Link>
            <Link
              to="/articoli/prodotti-migliori-itaobuy"
              className="flex items-center gap-3 text-sm font-label text-foreground-800 py-3 px-3 rounded-lg hover:bg-accent-100/40 transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              <i className="ri-star-line text-lg text-foreground-500"></i>
              Prodotti Migliori
            </Link>

            <div className="border-t border-background-200/70 my-3"></div>

            <p className="text-[10px] font-label text-foreground-400 uppercase tracking-wider px-3 mb-2">Categorie</p>

            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryNav(cat.url)}
                className="flex items-center gap-3 text-sm text-foreground-700 py-2.5 px-3 rounded-lg hover:bg-accent-100/40 transition-colors cursor-pointer text-left w-full"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></span>
                {cat.name}
              </button>
            ))}

            <div className="border-t border-background-200/70 my-3"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 text-sm font-label bg-primary-500 text-background-50 px-4 py-3 rounded-md hover:bg-primary-600 transition-colors cursor-pointer mx-3 mt-2"
            >
              <i className="ri-external-link-line"></i>
              buy it now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}